import React, { useState, useEffect } from 'react';
import {
  Autocomplete,
  TextField,
} from "@mui/material";

import {
  ResourceFeature
} from "@dndModels";

import API from "@utilities/smart_api";

import LoadingBox from "@components/input/LoadingBox";

interface Props {
  name: string;
  value?: string;
  values?: string[];
  multiple?: boolean;
  onChange?: (value: string) => void;
  onChangeValues?: (values: string[]) => void;
  extra_options?: string[];
  exclude?: string[];
  disabled?: boolean;
}

const api = API.getInstance();

/**
 * The names of the resources used by the loaded ResourceFeatures (not slots, those use the SpellSlotType _id).
 * This reads the hash directly instead of all_floating_features, because that filters to the user's
 * purchased/shared/free source books, which drops their homebrew and sourceless features even though they're loaded.
 */
export const resourceNameSuggestions = (): string[] => {
  // debugger;
  const names = (api.smart_hash.floating_feature ?? [])
    .filter(o => o instanceof ResourceFeature && !o.slots && o.type_id)
    .map(o => (o as ResourceFeature).type_id);
  return Array.from(new Set(names)).sort((a, b) => a.localeCompare(b));
}

/**
 * Resources are identified by their name, so this lets the user enter any name,
 * with suggestions from the ResourceFeatures that have been loaded.
 */
export default function SelectResourceBox({
  name,
  value = "",
  values = [],
  multiple = false,
  onChange,
  onChangeValues,
  extra_options = [],
  exclude = [],
  disabled = false
}: Props) {
  const [loading, setLoading] = useState(true);
  const [suggestions, setSuggestions] = useState<string[]>([]);

  useEffect(() => {
    api.loadDataType("floating_feature").then(() => {
      setSuggestions(resourceNameSuggestions());
    }).catch(ex => {
      console.error(ex);
    }).finally(() => {
      setLoading(false);
    });
  }, []);

  const current = multiple ? values : (value !== "" ? [value] : []);
  const options = Array.from(new Set([...extra_options, ...suggestions, ...current]))
    .filter(o => !exclude.includes(o));

  if (loading) {
    return (<LoadingBox />);
  } else if (multiple) {
    return (
      <Autocomplete
        freeSolo
        multiple
        autoSelect
        disableClearable
        options={options}
        value={values}
        disabled={disabled}
        renderInput={(params) => (
          <TextField {...params} label={name} />
        )}
        onChange={(_event: React.SyntheticEvent, new_values: string[]) => {
          if (onChangeValues) {
            onChangeValues(new_values.map(o => o.trim()).filter(o => o !== ""));
          }
        }}
      />
    );
  } else {
    return (
      <Autocomplete
        freeSolo
        autoSelect
        disableClearable
        options={options}
        value={value}
        disabled={disabled}
        renderInput={(params) => (
          <TextField {...params} label={name} />
        )}
        onChange={(_event: React.SyntheticEvent, new_value: string) => {
          if (onChange) {
            onChange(new_value.trim());
          }
        }}
      />
    );
  }
}
